import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import AddCommentToTask from 'actions/task/add_comment_to_task';
import connect from 'components/lib/connect';
import FullSerializer from 'components/text_editor/serializers/full_serializer';
import PlaintextSerializer from 'components/text_editor/serializers/plaintext_serializer';
import SlateEditor from './task_comment_slate_editor';
import { StyledAvatar } from './task_comment';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export function TaskCommentSlateEditor({
  agentAvatarUrl,
  agentName,
  className,
  isPending,
  itemId,
  lastOpenedAt,
  readOnly,
}) {
  const executeAction = useExecuteAction();
  const [slateComment, setSlateComment] = useState(() => FullSerializer.deserialize(''));

  let itemIdRef = useRef(itemId);
  useEffect(() => {
    itemIdRef.current = itemId;
  }, [itemId]);

  const editorRef = useRef(null);
  const onSlateChange = useCallback(e => setSlateComment(e.value), []);
  const onSlateKeyDown = useCallback(
    (evt, editor, next) => {
      if (evt.key === 'Enter' && !evt.shiftKey) {
        const plaintextComment = PlaintextSerializer.serialize(editor.value);
        if (plaintextComment.trim()) {
          executeAction(AddCommentToTask, {
            itemId: itemIdRef.current,
            message: FullSerializer.serialize(editor.value),
          });
          setSlateComment(FullSerializer.deserialize(''));
        }
        return true;
      }
      return next();
    },
    [executeAction]
  );

  useEffect(() => {
    // don't use shorthand bc `focus()` returns the editor value, and
    // useEffect is not allowed to return anything other than a cleanup function
    !isPending && editorRef.current && editorRef.current.focus();
  }, [isPending, lastOpenedAt]);

  return (
    <StyledEditorWrapper data-aid="taskCommentEditor-editor" className={className}>
      <StyledAvatar
        data-aid="taskCommentEditor-avatar"
        noWrapper
        profile={{ name: agentName, image: agentAvatarUrl }}
      />
      <StyledSlateEditor
        className="taskComment-slateEditor"
        editorRef={editorRef}
        onChange={onSlateChange}
        onKeyDown={onSlateKeyDown}
        placeholder="Comment on this task"
        readOnly={readOnly}
        value={slateComment}
      />
    </StyledEditorWrapper>
  );
}

const StyledSlateEditor = styled(SlateEditor)`
  border: 1px solid ${p => p.theme.colors.gray400};
  border-radius: ${p => p.theme.borderRadius.default};
  margin-left: ${p => p.theme.spacing.medium};
  outline: none;
  padding: 8px 12px;
  resize: none;
  width: 100%;
  &:focus {
    border-color: ${p => p.theme.colors.green400};
  }
`;

const StyledEditorWrapper = styled.div`
  display: flex;
  margin-top: ${p => p.theme.spacing.medium};
  textarea {
    border: 1px solid ${p => p.theme.colors.gray400};
    border-radius: ${p => p.theme.borderRadius.default};
    margin-left: ${p => p.theme.spacing.medium};
    outline: none;
    padding: 8px 12px;
    resize: none;
    width: 100%;
    &:focus {
      border-color: ${p => p.theme.colors.green400};
    }
  }
`;

TaskCommentSlateEditor.propTypes = {
  agentName: PropTypes.string,
  agentAvatarUrl: PropTypes.string,
  className: PropTypes.string,
  isPending: PropTypes.bool,
  lastOpenedAt: PropTypes.string,
  itemId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
};

function mapStateToProps({ getProvider, isFeatureEnabled }, { itemId }) {
  const agent = getProvider('currentAgent').get();
  const taskInfoProvider = getProvider('taskInfo');
  let isPending = true;
  if (taskInfoProvider.findBy({ id: itemId }) && !taskInfoProvider.isPending(itemId)) {
    isPending = false;
  }

  return {
    agentName: agent && (agent.name || agent.email),
    agentAvatarUrl: agent && agent.avatarUrl,
    isPending,
  };
}

const TaskCommentEditorContainer = connect(mapStateToProps)(TaskCommentSlateEditor);

export default TaskCommentEditorContainer;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/task_comments/task_comment_editor.jsx